import React, { useCallback, useContext, useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { Button, Table } from '../components';
import Frame from '../layout/Frame';
import { AppRoute, ButtonType, UserRole } from '../types/enums';
import useErrorHandler from '../hooks/useErrorHandler';
import { User } from '../types/types';
import useFilter from '../hooks/useFilter';
import { fetchUsers } from '../requests/requests';
import { UserContext } from '../App';
import { TableHeader } from '../components/Table/Table';
import Panel from '../layout/Panel';

const Users = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const errorHandler = useErrorHandler();
  const [users, setUsers] = useState<User[]>([]);
  const [query, setQuery] = useState('');
  const filteredUsers = useFilter(users, query);

  const refreshUsers = useCallback(() => {
    fetchUsers()
      .then((res) => {
        setUsers(res.data.users);
      })
      .catch(errorHandler);
  }, []);

  useEffect(() => {
    if (user?.role !== UserRole.ADMIN) {
      navigate(AppRoute.WEBSITES);
    } else {
      refreshUsers();
    }
  }, []);

  return (
    <Frame>
      <Panel>
        <h1>User management</h1>
        <TableHeader
          search={query}
          setSearch={setQuery}
          headers={HEADERS}
          columnSizes="auto auto 100px 100px 120px 80px"
        />
        <Table
          actionRenderer={Actions({
            edit: (user) => navigate(generatePath(AppRoute.USER_EDIT, { id: user.id }))
          })}
          data={filteredUsers}
          renderers={[Name, Email, Sites, Dashboards, Login]}
          columnSizes="auto auto 100px 100px 120px 80px"
          showOnMobile
        />
      </Panel>
    </Frame>
  );
};

const Actions: (actions: { edit: (user: User) => void }) => React.FC<User> =
  (actions) => (user) => {
    return (
      <Button action={() => actions.edit(user)} type={ButtonType.GHOST} spaceSaving small>
        Edit
      </Button>
    );
  };

const Name = ({ name }: User) => <strong>{name}</strong>;
const Email = ({ email }: User) => <strong>{email}</strong>;
const Login = ({ last_login }: User) => (
  <strong>{last_login ? new Date(last_login).toLocaleDateString() : '-'}</strong>
);
const Sites = ({ numberOfWebsites, plan, delta_website_count }: User) => (
  <strong>
    {numberOfWebsites} / {plan.websites}
    {delta_website_count && (
      <span style={{ color: delta_website_count > 0 ? '#14ca58' : '#d1192e' }}>
        &nbsp;({delta_website_count})
      </span>
    )}
  </strong>
);
const Dashboards = ({ numberOfDashboards, plan, delta_dashboard_count }: User) => (
  <strong>
    {numberOfDashboards} / {plan.dashboards}
    {delta_dashboard_count && (
      <span style={{ color: delta_dashboard_count > 0 ? '#14ca58' : '#d1192e' }}>
        &nbsp;({delta_dashboard_count})
      </span>
    )}
  </strong>
);

const HEADERS = ['name', 'email', '# websites', '# dashboards', 'last login', null];

export default Users;
